/** Property Drive — v1 transactions.js drivePanel/driveLoad, same contract:
 *
 *   GET /api/drive/files?property=ADDR&category=CAT[&kind=dispo] → {files}
 *   GET /api/drive/categories?property=ADDR[&kind=dispo]        → {counts}
 *
 * The server owns the Drive credentials and folder mapping; a 401 means
 * "Drive not connected" (the connect flow is Phase B), anything else that
 * fails is TRANSIENT ≠ DISCONNECTED — v1's 2026-07-07 lesson: a Render cold
 * start must show "waking up", never flip the panel to the connect state.
 * Uploads and trash are mutations — Phase B.
 */
import { BackendError, backendFetch, type BackendOptions } from "../../lib/backend";
import { API_BASE } from "../../config";

export interface DriveFile {
  id: string;
  name: string;
  /** preformatted by the server, v1 renders verbatim */
  size: string;
  date: string;
  by: string | null;
  webViewLink: string | null;
  thumbnailLink: string | null;
  /** Drive's mimeType (the server always sends it; older callers ignored it) */
  mimeType: string | null;
}

/** v1: flips get the 4 renovation categories; dispo (wholesale) deals get 2. */
export const DRIVE_CATS_FLIP = ["Before Pictures", "Renovation Photos", "Listing Pictures", "Documents"];
export const DRIVE_CATS_DISPO = ["Documents", "Photos"];

const isObj = (v: unknown): v is Record<string, unknown> =>
  v !== null && typeof v === "object" && !Array.isArray(v);
const str = (v: unknown): string => (typeof v === "string" ? v : "");

export function narrowDriveFile(raw: unknown): DriveFile | null {
  if (!isObj(raw)) return null;
  const id = str(raw["id"]);
  const name = str(raw["name"]);
  if (!id || !name) return null;
  return {
    id,
    name,
    size: str(raw["size"]),
    date: str(raw["date"]),
    by: str(raw["by"]) || null,
    webViewLink: str(raw["webViewLink"]) || null,
    thumbnailLink: str(raw["thumbnailLink"]) || null,
    mimeType: str(raw["mimeType"]) || null,
  };
}

/** v1 driveCard's doc-vs-image test. */
export const isDocFile = (name: string): boolean => /\.(pdf|docx?|xlsx?)$/i.test(name);

// ---- PREVIEW WITHOUT GOOGLE'S VIEWER (2026-09-24) ---------------------------
// Tom: "on my phone … when i want to preview a file from the drive it tells
// me to go to the drive, i cant see a preview like i can on the computer".
// The preview was Google's embedded viewer (drive.google.com/file/d/…/preview)
// in an iframe — on a phone Google answers that with its "open in the Drive
// app" page instead of the file. So photos and PDFs now come through OUR
// backend (the same Drive connection that lists them) and render natively:
// an <img>, or the browser's own PDF viewer. Google's viewer stays only for
// what browsers can't draw themselves (Word/Excel/video) on desktop.

export type PreviewKind = "image" | "pdf" | "gdoc" | "other";

export function previewKind(f: Pick<DriveFile, "name" | "mimeType">): PreviewKind {
  const m = (f.mimeType || "").toLowerCase();
  if (m.startsWith("image/") || /\.(jpe?g|png|gif|webp|heic|heif|bmp|avif)$/i.test(f.name)) return "image";
  if (m === "application/pdf" || /\.pdf$/i.test(f.name)) return "pdf";
  if (m.startsWith("application/vnd.google-apps")) return "gdoc"; // Docs/Sheets — server exports a PDF
  return "other";
}

/** Drive's thumbnail URL is a signed lh3 link ending in `=s220`; the size
 * suffix is free to change, and a big one is a real first-page render of a
 * photo, a PDF or a Word file — no sign-in needed. Instant placeholder
 * while the real bytes load, and the whole preview for kinds we can't draw. */
export function bigThumb(f: Pick<DriveFile, "thumbnailLink">, px = 1600): string | null {
  const t = f.thumbnailLink;
  if (!t) return null;
  return /=s\d+(-[a-z]+)*$/i.test(t) ? t.replace(/=s\d+(-[a-z]+)*$/i, `=s${px}`) : t;
}

/** Phone PDF preview: the server rasterizes pages (app/pdfpages.py) and the
 * app stacks them — iOS draws only page 1 of an iframe'd PDF, Android Chrome
 * downloads it. `shown` ≤ count: past that the app offers the full file. */
export async function fetchPdfPages(token: string | null, fileId: string, signal?: AbortSignal): Promise<{ count: number; shown: number }> {
  const j = await backendFetch<{ count?: unknown; shown?: unknown }>(token, `/api/drive/pdf-pages/${encodeURIComponent(fileId)}`, { timeoutMs: 60000, signal });
  const count = typeof j?.count === "number" ? j.count : 0;
  const shown = typeof j?.shown === "number" ? j.shown : count;
  return { count, shown };
}

export async function fetchPdfPage(token: string | null, fileId: string, n: number, width: number, signal?: AbortSignal): Promise<Blob> {
  const ctl = new AbortController();
  const t = setTimeout(() => ctl.abort(), 60000);
  signal?.addEventListener("abort", () => ctl.abort());
  try {
    const w = Math.max(200, Math.min(2400, Math.round(width)));
    const res = await fetch(`${API_BASE}/api/drive/pdf-page/${encodeURIComponent(fileId)}/${n}?w=${w}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      signal: ctl.signal,
    });
    if (!res.ok) throw new BackendError(res.status, `page ${n} failed (${res.status})`);
    return await res.blob();
  } finally {
    clearTimeout(t);
  }
}

/** The file's bytes, through the backend (Authorization header, so nothing
 * secret rides a URL). Google-native files come back as a PDF export. */
export async function fetchDriveBlob(token: string | null, fileId: string, signal?: AbortSignal): Promise<{ blob: Blob; mime: string }> {
  const ctl = new AbortController();
  const t = setTimeout(() => ctl.abort(), 60000);
  signal?.addEventListener("abort", () => ctl.abort());
  try {
    const res = await fetch(`${API_BASE}/api/drive/download/${encodeURIComponent(fileId)}?inline=1`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      signal: ctl.signal,
    });
    if (!res.ok) throw new BackendError(res.status, `download failed (${res.status})`);
    const blob = await res.blob();
    return { blob, mime: res.headers.get("Content-Type") || blob.type || "application/octet-stream" };
  } finally {
    clearTimeout(t);
  }
}

export type DriveLoad =
  | { kind: "ok"; files: DriveFile[] }
  | { kind: "disconnected" } // real 401 — connect flow is Phase B
  | { kind: "transient" }; //   cold start / network — show waking, retry

export async function fetchDriveFiles(
  token: string | null,
  address: string,
  category: string,
  dispo: boolean,
  opts?: Pick<BackendOptions, "signal" | "onWake">,
): Promise<DriveLoad> {
  try {
    const j = await backendFetch<{ files?: unknown }>(
      token,
      `/api/drive/files?property=${encodeURIComponent(address)}&category=${encodeURIComponent(category)}${dispo ? "&kind=dispo" : ""}`,
      { timeoutMs: 30000, ...opts },
    );
    return {
      kind: "ok",
      files: Array.isArray(j?.files) ? (j.files as unknown[]).map(narrowDriveFile).filter((f): f is DriveFile => f !== null) : [],
    };
  } catch (e) {
    if (e instanceof BackendError && e.status === 401) return { kind: "disconnected" };
    return { kind: "transient" };
  }
}

export async function fetchDriveCategories(
  token: string | null,
  address: string,
  dispo: boolean,
  opts?: Pick<BackendOptions, "signal">,
): Promise<Record<string, number>> {
  try {
    const j = await backendFetch<{ counts?: unknown }>(
      token,
      `/api/drive/categories?property=${encodeURIComponent(address)}${dispo ? "&kind=dispo" : ""}`,
      { timeoutMs: 20000, ...opts },
    );
    if (!isObj(j?.counts)) return {};
    const out: Record<string, number> = {};
    for (const [k, v] of Object.entries(j.counts)) if (typeof v === "number") out[k] = v;
    return out;
  } catch {
    return {};
  }
}

export const DRIVE_MSG = {
  waking: "The free server sleeps when idle — retrying automatically. Your files are safe in Google Drive.",
  connectPhaseB: "Connect Google Drive — unlocks in the live-fire session",
  uploadPhaseB: "Uploads unlock in the live-fire session",
  emptyCat: (cat: string) => `No files yet in ${cat}`,
  synced: "Synced with Google Drive",
  syncing: "Syncing…",
  loading: "Loading from Google Drive…",
  uploading: "Uploading to Google Drive…",
  uploadOk: "Uploaded ✓",
  uploadFail: "Upload failed — nothing was saved. Try again.",
  foldersOk: "Created the deal's Drive folders",
  foldersSkip: "Connect Google Drive (Business Files) to auto-create deal folders",
};

// ---- live-fire (Phase B): folder creation on deal-create, and file upload ----

/** v1 createDriveTxnFolders → POST /api/drive/transaction-folders {address}.
 * Best-effort by design (v1 also creates folders lazily on first upload), so
 * the caller treats a failure as a tip, never a hard error. HONEST: throws on
 * a non-2xx so the caller can tell "connected + made" from "not connected". */
export async function createTransactionFolders(
  token: string | null,
  address: string,
  opts: BackendOptions = {},
): Promise<void> {
  await backendFetch(token, "/api/drive/transaction-folders", {
    method: "POST",
    body: { address },
    timeoutMs: 30000,
    ...(opts.onWake ? { onWake: opts.onWake } : {}),
    ...(opts.signal ? { signal: opts.signal } : {}),
  });
}

export interface DriveUpload {
  address: string;
  category: string;
  dispo: boolean;
  file: File;
}

/** v1 upload-queue → multipart POST /api/drive/upload. HONEST: resolves only on
 * a 2xx, throws otherwise (never a false "uploaded"). Auth via a `token` FORM
 * FIELD, not a header — a custom header forces a CORS preflight the upload path
 * isn't set up for (v1's proven approach). Single attempt: no blind retry that
 * could upload the same file twice. */
export async function uploadDriveFile(token: string | null, u: DriveUpload, signal?: AbortSignal): Promise<void> {
  const fd = new FormData();
  fd.append("property", u.address);
  fd.append("category", u.category || "Documents");
  if (u.dispo) fd.append("kind", "dispo");
  if (token) fd.append("token", token);
  fd.append("file", u.file, u.file.name);
  const ctl = new AbortController();
  const onAbort = () => ctl.abort();
  signal?.addEventListener("abort", onAbort);
  const t = setTimeout(() => ctl.abort(), 120000); // large files
  try {
    const res = await fetch(API_BASE + "/api/drive/upload", { method: "POST", body: fd, signal: ctl.signal });
    if (!res.ok) {
      let detail = String(res.status);
      try {
        const j = await res.json();
        if (j && typeof j.detail === "string") detail = j.detail;
      } catch { /* keep status */ }
      throw new BackendError(res.status, detail.slice(0, 160));
    }
  } finally {
    clearTimeout(t);
    signal?.removeEventListener("abort", onAbort);
  }
}
